import { MF } from "../components/NumberInputs";
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import Modal, { FormRow } from '../components/Modal';

/* local FormSection — not exported by Modal.js */
const FormSection = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#8892a5', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
    {children}
  </div>
);

/* ───────────────────────── palette / tokens ───────────────────────── */
const C = {
  bg:       '#0a0f1a',
  card:     '#111827',
  border:   '#1e2a3a',
  text:     '#e2e8f0',
  muted:    '#8892a5',
  accent:   '#3b82f6',
  green:    '#10b981',
  yellow:   '#f59e0b',
  red:      '#ef4444',
  orange:   '#f97316',
  purple:   '#a855f7',
  rowHover: '#1a2332',
};

const s = {
  page:       { padding: 24, color: C.text, minHeight: '100vh', background: C.bg },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title:      { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle:   { color: C.muted, fontSize: 13, marginTop: 4 },
  section:    { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 24, overflow: 'hidden' },
  sectionHdr: { padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sectionTtl: { fontSize: 15, fontWeight: 600, margin: 0 },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:         { textAlign: 'left', padding: '10px 14px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  td:         { padding: '10px 14px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  btn:        { padding: '7px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#fff' },
  btnPrimary: { background: C.accent },
  btnSmall:   { padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#fff' },
  badge:      (color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: '#fff', background: color }),
  money:      { fontFamily: '"SF Mono", "Fira Code", monospace', textAlign: 'right' },
  emptyRow:   { padding: 32, textAlign: 'center', color: C.muted, fontSize: 13 },
  input:      { width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#0d1320', color: C.text, fontSize: 13 },
  select:     { width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#0d1320', color: C.text, fontSize: 13 },
  kpiRow:     { display: 'flex', gap: 16, marginBottom: 24 },
  kpiCard:    { flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px' },
  kpiLabel:   { fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 },
  kpiValue:   { fontSize: 20, fontWeight: 700 },
  tabBar:     { display: 'flex', gap: 0, marginBottom: 0, borderBottom: `1px solid ${C.border}` },
  tab:        (active) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600, border: 'none', background: 'transparent',
    color: active ? C.accent : C.muted, borderBottom: active ? `2px solid ${C.accent}` : '2px solid transparent',
    transition: 'all 0.15s',
  }),
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, padding: 16, fontSize: 12 },
  detailLabel: { color: C.muted, marginBottom: 2 },
  detailValue: { fontWeight: 600 },
};

const fmt = (v) => v != null ? parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtPct = (v) => v != null ? `${parseFloat(v).toFixed(2)}%` : '—';
const fmtInt = (v) => v != null ? parseInt(v).toLocaleString() : '—';
const fmtDate = (v) => v || '—';

const STATUS_CONFIG = {
  RECEIVED:   { label: 'Received',   color: C.yellow,  icon: '📩' },
  PRICING:    { label: 'In Pricing', color: C.orange,  icon: '📊' },
  PRICED:     { label: 'Priced',     color: C.accent,  icon: '✓' },
  SETTLED:    { label: 'Settled',    color: C.green,   icon: '✅' },
  CANCELLED:  { label: 'Cancelled',  color: C.red,     icon: '✕' },
};
const TABS = ['ALL', 'RECEIVED', 'PRICING', 'PRICED', 'SETTLED', 'CANCELLED'];

/* ─────────────────────── ELOC DRAWS PAGE ──────────────────────────── */
export default function ElocDraws() {
  const [notices, setNotices] = useState([]);
  const [deals, setDeals] = useState([]);
  const [securities, setSecurities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* UI state */
  const [activeTab, setActiveTab] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);

  /* modals */
  const [newModal, setNewModal] = useState(false);
  const [settleModal, setSettleModal] = useState({ open: false, notice: null });
  const [newForm, setNewForm] = useState({ deal: '', notice_date: '', requested_shares: '', drawdown_tier: '1' });
  const [settleForm, setSettleForm] = useState({});
  const [saving, setSaving] = useState(false);

  /* ── data fetch ───────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [noticeRes, dealRes, secRes] = await Promise.all([
        api.get('/positions/eloc-notices/'),
        api.get('/securities/deals/'),
        api.get('/securities/securities/'),
      ]);
      const secs = (secRes.data.results || secRes.data);
      const elocSecIds = new Set(secs.filter(s => s.instrument_type === 'ELOC').map(s => s.id));
      const elocDeals = (dealRes.data.results || dealRes.data).filter(d => elocSecIds.has(d.security));

      setSecurities(secs);
      setDeals(elocDeals);
      setNotices(noticeRes.data.results || noticeRes.data);
    } catch (e) {
      console.error(e);
      setError('Failed to load ELOC draw data.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── helpers ──────────────────────────────────────────────────── */
  const dealById = Object.fromEntries(deals.map(d => [d.id, d]));
  const secById = Object.fromEntries(securities.map(s => [s.id, s]));

  const enriched = notices.map(n => {
    const deal = dealById[n.deal] || {};
    const sec = secById[deal.security] || {};
    return { ...n, deal_name: deal.deal_name || '—', ticker: sec.ticker || '—', sec };
  });

  const filtered = activeTab === 'ALL' ? enriched : enriched.filter(n => (n.status || '').toUpperCase() === activeTab);

  /* KPIs */
  const activeCount = enriched.filter(n => ['RECEIVED', 'PRICING', 'PRICED'].includes((n.status || '').toUpperCase())).length;
  const settledCount = enriched.filter(n => (n.status || '').toUpperCase() === 'SETTLED').length;
  const totalSettledAmt = enriched.filter(n => (n.status || '').toUpperCase() === 'SETTLED').reduce((sum, n) => sum + parseFloat(n.settlement_amount || 0), 0);
  const totalRequestedShares = enriched.filter(n => ['RECEIVED', 'PRICING', 'PRICED'].includes((n.status || '').toUpperCase())).reduce((sum, n) => sum + parseFloat(n.requested_shares || 0), 0);

  /* ── New Put Notice ──────────────────────────────────────────── */
  const openNew = () => {
    setNewForm({
      deal: deals.length > 0 ? deals[0].id : '',
      notice_date: new Date().toISOString().split('T')[0],
      requested_shares: '',
      drawdown_tier: '1',
    });
    setNewModal(true);
  };

  const submitNew = async () => {
    if (!newForm.deal || !newForm.requested_shares) { alert('Deal and requested shares are required.'); return; }
    setSaving(true);
    try {
      await api.post('/positions/eloc-notices/receive-notice/', {
        deal_id: parseInt(newForm.deal),
        notice_date: newForm.notice_date,
        requested_shares: newForm.requested_shares,
        drawdown_tier: parseInt(newForm.drawdown_tier),
      });
      setNewModal(false);
      load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  /* ── Settle Notice ───────────────────────────────────────────── */
  const openSettle = (notice) => {
    setSettleForm({
      vwap: notice.vwap || '',
      discount_price: notice.discount_price || '',
      settlement_shares: notice.settlement_shares || notice.requested_shares || '',
      settlement_amount: notice.settlement_amount || '',
      settlement_date: notice.settlement_date || new Date().toISOString().split('T')[0],
    });
    setSettleModal({ open: true, notice });
  };

  const submitSettle = async () => {
    setSaving(true);
    try {
      await api.post(`/positions/eloc-notices/${settleModal.notice.id}/settle/`, settleForm);
      setSettleModal({ open: false, notice: null });
      load();
    } catch (e) {
      alert('Settle failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  /* ── Cancel Notice ───────────────────────────────────────────── */
  const cancelNotice = async (id) => {
    if (!window.confirm('Cancel this put notice? This cannot be undone.')) return;
    try {
      await api.post(`/positions/eloc-notices/${id}/cancel/`);
      load();
    } catch (e) {
      alert('Cancel failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    }
  };

  /* status badge helper */
  const statusBadge = (status) => {
    const key = (status || '').toUpperCase();
    const cfg = STATUS_CONFIG[key] || { label: status, color: C.muted };
    return <span style={s.badge(cfg.color)}>{cfg.icon} {cfg.label}</span>;
  };

  /* ── RENDER ──────────────────────────────────────────────────── */
  if (loading) return <div style={s.page}><p style={{ color: C.muted }}>Loading ELOC draws…</p></div>;
  if (error) return <div style={s.page}><p style={{ color: C.red }}>{error}</p></div>;

  return (
    <div style={s.page}>
      {/* header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>ELOC ELOC Put Notices</h1>
          <div style={s.subtitle}>Put notice management — receive, price, settle, and track ELOC drawdowns</div>
        </div>
        <button style={{ ...s.btn, ...s.btnPrimary }} onClick={openNew}>+ New Put Notice</button>
      </div>

      {/* KPIs */}
      <div style={s.kpiRow}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Active Notices</div>
          <div style={{ ...s.kpiValue, color: C.yellow }}>{activeCount}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Shares in Pipeline</div>
          <div style={{ ...s.kpiValue, color: C.orange }}>{fmtInt(totalRequestedShares)}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Settled Draws</div>
          <div style={{ ...s.kpiValue, color: C.green }}>{settledCount}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total Settled ($)</div>
          <div style={{ ...s.kpiValue, color: C.green }}>${fmt(totalSettledAmt)}</div>
        </div>
      </div>

      {/* ── NOTICES TABLE ──────────────────────────────────────── */}
      <div style={s.section}>
        {/* Tab bar */}
        <div style={{ ...s.sectionHdr, padding: 0, borderBottom: 'none' }}>
          <div style={s.tabBar}>
            {TABS.map(tab => (
              <button key={tab} style={s.tab(activeTab === tab)} onClick={() => setActiveTab(tab)}>
                {tab === 'ALL' ? `All (${enriched.length})` : `${STATUS_CONFIG[tab]?.label || tab} (${enriched.filter(n => (n.status||'').toUpperCase() === tab).length})`}
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}></th>
                <th style={s.th}>Notice Date</th>
                <th style={s.th}>Deal / Ticker</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Tier</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Requested Shares</th>
                <th style={s.th}>Pricing Window</th>
                <th style={{ ...s.th, textAlign: 'right' }}>VWAP</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Discount Price</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Settle Shares</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Settle Amount</th>
                <th style={s.th}>Settle Date</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={13} style={s.emptyRow}>
                  {activeTab === 'ALL' ? 'No put notices yet. Click "+ New Put Notice" to create one.' : `No notices with status "${activeTab}".`}
                </td></tr>
              ) : filtered.map(n => {
                const st = (n.status || '').toUpperCase();
                const isExpanded = expandedId === n.id;
                return (
                  <React.Fragment key={n.id}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = C.rowHover}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      onClick={() => setExpandedId(isExpanded ? null : n.id)}
                    >
                      <td style={{ ...s.td, width: 28, textAlign: 'center', color: C.muted }}>{isExpanded ? '▾' : '▸'}</td>
                      <td style={{ ...s.td, fontWeight: 600 }}>{fmtDate(n.notice_date)}</td>
                      <td style={s.td}>
                        <span style={{ color: C.accent, fontWeight: 600 }}>{n.ticker}</span>
                        <span style={{ color: C.muted, marginLeft: 6 }}>{n.deal_name}</span>
                      </td>
                      <td style={s.td}>{statusBadge(n.status)}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{n.drawdown_tier || '—'}</td>
                      <td style={{ ...s.td, ...s.money }}>{fmtInt(n.requested_shares)}</td>
                      <td style={s.td}>
                        {n.pricing_start_date
                          ? `${fmtDate(n.pricing_start_date)} → ${fmtDate(n.pricing_end_date)}`
                          : '—'}
                      </td>
                      <td style={{ ...s.td, ...s.money }}>{n.vwap ? `$${fmt(n.vwap)}` : '—'}</td>
                      <td style={{ ...s.td, ...s.money }}>{n.discount_price ? `$${fmt(n.discount_price)}` : '—'}</td>
                      <td style={{ ...s.td, ...s.money }}>{n.settlement_shares ? fmtInt(n.settlement_shares) : '—'}</td>
                      <td style={{ ...s.td, ...s.money }}>{n.settlement_amount ? `$${fmt(n.settlement_amount)}` : '—'}</td>
                      <td style={s.td}>{fmtDate(n.settlement_date)}</td>
                      <td style={s.td} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {['RECEIVED', 'PRICING', 'PRICED'].includes(st) && (
                            <button style={{ ...s.btnSmall, background: C.green }} onClick={() => openSettle(n)}>Settle</button>
                          )}
                          {['RECEIVED', 'PRICING'].includes(st) && (
                            <button style={{ ...s.btnSmall, background: C.red }} onClick={() => cancelNotice(n.id)}>Cancel</button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={13} style={{ padding: 0, background: '#0d1320', borderBottom: `1px solid ${C.border}` }}>
                          <div style={s.detailGrid}>
                            <div>
                              <div style={s.detailLabel}>Notice ID</div>
                              <div style={s.detailValue}>#{n.id}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Deal</div>
                              <div style={s.detailValue}>{n.deal_name}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Ticker</div>
                              <div style={{ ...s.detailValue, color: C.accent }}>{n.ticker}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Drawdown Tier</div>
                              <div style={s.detailValue}>{n.drawdown_tier || '—'}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Pricing Start</div>
                              <div style={s.detailValue}>{fmtDate(n.pricing_start_date)}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Pricing End</div>
                              <div style={s.detailValue}>{fmtDate(n.pricing_end_date)}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Pricing Days (Facility)</div>
                              <div style={s.detailValue}>{n.sec?.eloc_pricing_days || '—'}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Discount % (Tier {n.drawdown_tier || 1})</div>
                              <div style={s.detailValue}>
                                {n.drawdown_tier === 2 ? fmtPct(n.sec?.eloc_discount_pct_2)
                                  : n.drawdown_tier === 3 ? fmtPct(n.sec?.eloc_discount_pct_3)
                                  : fmtPct(n.sec?.eloc_discount_pct)}
                              </div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>VWAP</div>
                              <div style={s.detailValue}>{n.vwap ? `$${fmt(n.vwap)}` : 'Pending'}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Discount Price</div>
                              <div style={s.detailValue}>{n.discount_price ? `$${fmt(n.discount_price)}` : 'Pending'}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Settlement Shares</div>
                              <div style={s.detailValue}>{n.settlement_shares ? fmtInt(n.settlement_shares) : 'Pending'}</div>
                            </div>
                            <div>
                              <div style={s.detailLabel}>Settlement Amount</div>
                              <div style={s.detailValue}>{n.settlement_amount ? `$${fmt(n.settlement_amount)}` : 'Pending'}</div>
                            </div>
                          </div>
                          {st === 'SETTLED' && n.settlement_lot && (
                            <div style={{ padding: '0 16px 16px', fontSize: 12 }}>
                              <span style={{ color: C.muted }}>Settlement Lot: </span>
                              <span style={{ color: C.green, fontWeight: 600 }}>#{n.settlement_lot}</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── NEW PUT NOTICE MODAL ───────────────────────────────── */}
      {newModal && <Modal
        isOpen={true}
        onClose={() => setNewModal(false)}
        title="New Put Notice"
        onSubmit={submitNew}
        submitLabel={saving ? 'Submitting…' : 'Submit Put Notice'}
        submitDisabled={saving}
        width={480}
      >
        <FormSection title="Notice Details">
          <FormRow>
            <label>ELOC Deal
              <select style={s.select} value={newForm.deal} onChange={e => setNewForm(f => ({ ...f, deal: e.target.value }))}>
                <option value="">— Select Deal —</option>
                {deals.map(d => {
                  const sec = secById[d.security] || {};
                  return <option key={d.id} value={d.id}>{sec.ticker} — {d.deal_name}</option>;
                })}
              </select>
            </label>
          </FormRow>
          <FormRow>
            <label>Notice Date
              <input style={s.input} type="date" value={newForm.notice_date} onChange={e => setNewForm(f => ({ ...f, notice_date: e.target.value }))} />
            </label>
            <label>Drawdown Tier
              <select style={s.select} value={newForm.drawdown_tier} onChange={e => setNewForm(f => ({ ...f, drawdown_tier: e.target.value }))}>
                <option value="1">Tier 1</option>
                <option value="2">Tier 2</option>
                <option value="3">Tier 3</option>
              </select>
            </label>
          </FormRow>
          <FormRow>
            <label>Requested Shares
              <MF style={s.input} value={newForm.requested_shares} onChange={v => setNewForm(f => ({ ...f, requested_shares: v }))} placeholder="500,000" />
            </label>
          </FormRow>
        </FormSection>

        {/* show facility context */}
        {newForm.deal && (() => {
          const deal = dealById[parseInt(newForm.deal)];
          const sec = deal ? secById[deal.security] : null;
          if (!sec) return null;
          const tier = parseInt(newForm.drawdown_tier);
          const disc = tier === 2 ? sec.eloc_discount_pct_2 : tier === 3 ? sec.eloc_discount_pct_3 : sec.eloc_discount_pct;
          return (
            <div style={{ background: '#0d1320', border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginTop: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: C.accent }}>Facility Reference</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div><span style={{ color: C.muted }}>Max Advance: </span>${fmt(sec.eloc_max_advance)}</div>
                <div><span style={{ color: C.muted }}>Discount (T{tier}): </span>{fmtPct(disc)}</div>
                <div><span style={{ color: C.muted }}>Pricing Days: </span>{sec.eloc_pricing_days || '—'}</div>
                <div><span style={{ color: C.muted }}>Settlement Days: </span>{sec.eloc_settlement_days || '—'}</div>
                <div><span style={{ color: C.muted }}>Ownership Limit: </span>{fmtPct(sec.eloc_ownership_limit_pct)}</div>
                <div><span style={{ color: C.muted }}>Exchange Cap: </span>{fmtPct(sec.eloc_exchange_cap_pct)}</div>
              </div>
            </div>
          );
        })()}
      </Modal>}

      {/* ── SETTLE NOTICE MODAL ────────────────────────────────── */}
      {settleModal.open && <Modal
        isOpen={true}
        onClose={() => setSettleModal({ open: false, notice: null })}
        title={`Settle Put Notice — ${settleModal.notice?.ticker || ''} #${settleModal.notice?.id || ''}`}
        onSubmit={submitSettle}
        submitLabel={saving ? 'Settling…' : 'Confirm Settlement'}
        submitDisabled={saving}
        width={520}
      >
        <FormSection title="Pricing">
          <FormRow>
            <label>VWAP ($)
              <MF style={s.input} value={settleForm.vwap || ''} onChange={v => setSettleForm(f => ({ ...f, vwap: v }))} />
            </label>
            <label>Discount Price ($)
              <MF style={s.input} value={settleForm.discount_price || ''} onChange={v => setSettleForm(f => ({ ...f, discount_price: v }))} />
            </label>
          </FormRow>
        </FormSection>
        <FormSection title="Settlement">
          <FormRow>
            <label>Settlement Shares
              <MF style={s.input} value={settleForm.settlement_shares || ''} onChange={v => setSettleForm(f => ({ ...f, settlement_shares: v }))} />
            </label>
            <label>Settlement Amount ($)
              <MF style={s.input} value={settleForm.settlement_amount || ''} onChange={v => setSettleForm(f => ({ ...f, settlement_amount: v }))} />
            </label>
          </FormRow>
          <FormRow>
            <label>Settlement Date
              <input style={s.input} type="date" value={settleForm.settlement_date || ''} onChange={e => setSettleForm(f => ({ ...f, settlement_date: e.target.value }))} />
            </label>
          </FormRow>
        </FormSection>

        {/* auto-calc helper */}
        {settleForm.vwap && settleModal.notice && (() => {
          const notice = settleModal.notice;
          const tier = notice.drawdown_tier || 1;
          const sec = notice.sec || {};
          const disc = tier === 2 ? sec.eloc_discount_pct_2 : tier === 3 ? sec.eloc_discount_pct_3 : sec.eloc_discount_pct;
          const vwap = parseFloat(settleForm.vwap);
          const calcDiscount = disc ? vwap * (1 - parseFloat(disc) / 100) : null;
          const shares = parseFloat(notice.requested_shares || 0);
          const calcAmount = calcDiscount ? (shares * calcDiscount) : null;
          return (
            <div style={{ background: '#0d1320', border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginTop: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: C.yellow }}>Calculated Values (Tier {tier}, {fmtPct(disc)} discount)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><span style={{ color: C.muted }}>Calc Discount Price: </span>{calcDiscount ? `$${fmt(calcDiscount)}` : '—'}</div>
                <div><span style={{ color: C.muted }}>Calc Settlement Amt: </span>{calcAmount ? `$${fmt(calcAmount)}` : '—'}</div>
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  style={{ ...s.btnSmall, background: C.yellow, color: '#000' }}
                  onClick={() => setSettleForm(f => ({
                    ...f,
                    discount_price: calcDiscount ? calcDiscount.toFixed(4) : f.discount_price,
                    settlement_amount: calcAmount ? calcAmount.toFixed(2) : f.settlement_amount,
                    settlement_shares: shares.toString(),
                  }))}
                >
                  Apply Calculated Values
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>}
    </div>
  );
}
